import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import { asmrResources } from '@/mocks/asmrResources';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const resources = asmrResources.filter((r) => r.category === slug);

  if (!category) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ASMR导航`,
    description: category.description,
    url: `https://asmr-navigation.com/category/${slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-navigation.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://asmr-navigation.com/category/${slug}` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${category.name}类asmr网站有哪些推荐？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: category.description,
        },
      },
    ],
  };

  return (
    <Layout>
      <SEO
        title={`${category.name} — ASMR资源推荐`}
        description={category.description}
        keywords={`asmr网站,asmr网站推荐,${category.name},asmr助眠网站,asmr在线听,asmr资源平台`}
        canonicalUrl={`/category/${slug}`}
        ogType="website"
        schema={{ ...categorySchema, ...breadcrumbSchema, ...faqSchema }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Category Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className={`${category.icon} text-lg`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {category.name}
            </h1>
          </div>
          <p className="text-sm text-foreground-500 max-w-2xl leading-relaxed">
            {category.description}
          </p>
          <p className="text-xs text-foreground-400 mt-2">
            共找到 <strong className="text-primary-600">{resources.length}</strong> 个相关<strong>asmr网站</strong>
          </p>
        </div>

        {/* Sub-category quick links */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                c.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        {/* Resource Grid */}
        {resources.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
            {resources.map((resource) => (
              <a
                key={resource.id}
                href={resource.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group block bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
                data-product-shop
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={resource.imageUrl}
                    alt={resource.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={`${resource.name} - asmr网站推荐`}
                  />
                  <div className="absolute top-3 right-3 bg-background-50/90 backdrop-blur-sm px-2 py-0.5 rounded-md text-xs font-medium text-primary-700">
                    ★ {resource.rating}
                  </div>
                </div>
                <div className="p-4">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {resource.name}
                  </h2>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-3 mb-3">
                    {resource.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {resource.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
              <i className="ri-inbox-line text-2xl"></i>
            </div>
            <p className="text-foreground-500">该分类暂无资源，请浏览其他分类</p>
          </div>
        )}

        {/* Bottom SEO text */}
        <div className="mt-12 p-6 bg-background-100 rounded-lg">
          <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
            关于{category.name}类<strong>asmr网站</strong>
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed">
            {category.name}是ASMR领域中备受欢迎的类型之一。通过本页整理的<strong>asmr网站推荐</strong>，
            你可以快速找到高品质的{category.name}类<strong>asmr资源平台</strong>。
            每个推荐的<strong>asmr网站</strong>都经过我们团队的严格筛选，
            确保为用户提供最佳的<strong>asmr在线听</strong>体验。
            如果你正在寻找最好的{category.name}<strong>asmr助眠网站</strong>，
            本页面的推荐列表将是你最好的选择。
          </p>
        </div>
      </div>
    </Layout>
  );
}